import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  DialogContent,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_get_rag_items,
  API_create_rag_item,
  API_update_rag_item,
  API_delete_rag_item,
  API_reembed_rag_items,
  API_get_system_prompt,
  API_create_system_prompt,
  API_get_system_prompt_history,
  API_activate_system_prompt,
  API_chat_test_send,
  API_chat_test_clear,
} from "./utils/http";
import { AlertCus } from "./components/AlertCus";
import { DialogCus } from "./components/DialogCus";
import AppBarSearchBox from "./components/SearchBox";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import PatternResponse from "./PatternResponse";

const COLUMNS_RAG = [
  { field: "id", headerName: "ID", width: 70 },
  {
    field: "text",
    headerName: "Text",
    flex: 1,
    minWidth: 250,
    renderCell: (params) => (
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
        title={params.value}
      >
        {params.value}
      </span>
    ),
  },
  {
    field: "source",
    headerName: "Source",
    width: 120,
    renderCell: (params) => (
      <Chip label={params.value} size="small" variant="outlined" />
    ),
  },
  {
    field: "type",
    headerName: "Type",
    width: 120,
    renderCell: (params) => (
      <Chip label={params.value} size="small" color="primary" variant="outlined" />
    ),
  },
  {
    field: "created_at",
    headerName: "Created",
    width: 150,
    renderCell: (params) => {
      if (!params.value) return "";
      let d = new Date(params.value);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    },
  },
];

const btnStyle = {
  borderRadius: "20px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: 13,
};

export default function AIChat() {
  const [view, setView] = useState("rag");
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [rowSelection, setRowSelection] = useState([]);
  const [density, setDensity] = useState("comfortable");

  // filters
  const [filterSource, setFilterSource] = useState("");
  const [filterType, setFilterType] = useState("");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  // dialogs
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  // form fields
  const [formText, setFormText] = useState("");
  const [formSource, setFormSource] = useState("");
  const [formType, setFormType] = useState("");

  // system prompt
  const [systemPrompt, setSystemPrompt] = useState("");
  const [activeVersion, setActiveVersion] = useState(null);
  const [promptHistory, setPromptHistory] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isDefault, setIsDefault] = useState(true);

  // chat playground
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatSending, setChatSending] = useState(false);
  const [chatParsed, setChatParsed] = useState({});
  const chatEndRef = React.useRef(null);

  // status
  const [apiResult, setApiResult] = useState(null);
  const [apiMessage, setApiMessage] = useState("");

  const fetchFilterOptions = async () => {
    await API_get_rag_items({ limit: 1000 }, (res) => {
      let items = res.items || [];
      setSourceOptions([...new Set(items.map((i) => i.source).filter(Boolean))]);
      setTypeOptions([...new Set(items.map((i) => i.type).filter(Boolean))]);
    });
  };

  const fetchData = async () => {
    let params = { limit: 1000 };
    if (filterSource) params.source = filterSource;
    if (filterType) params.type = filterType;

    await API_get_rag_items(params, (res) => {
      let items = res.items || [];
      setData(items);
      setTotal(res.total || items.length);
    });
  };

  const fetchPrompt = async () => {
    await API_get_system_prompt((res) => {
      if (!res.error) {
        setSystemPrompt(res.prompt || "");
        if (res.source === "default") {
          setIsDefault(true);
          setActiveVersion(null);
        } else {
          setIsDefault(false);
          setActiveVersion(res.version || null);
        }
      }
    });
  };

  const fetchHistory = async () => {
    await API_get_system_prompt_history({ limit: 50 }, (res) => {
      setPromptHistory(res.items || []);
    });
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterSource, filterType]);

  useEffect(() => {
    if (view === "prompt") {
      fetchPrompt();
      fetchHistory();
    }
  }, [view]);

  const getSelectedItem = () => {
    if (!data || !selectedId) return null;
    return data.find((item) => item.id === selectedId);
  };

  const openAddDialog = () => {
    setFormText("");
    setFormSource("");
    setFormType("");
    setOpenAdd(true);
  };

  const openEditDialog = () => {
    let item = getSelectedItem();
    if (!item) return;
    setFormText(item.text || "");
    setFormSource(item.source || "");
    setFormType(item.type || "");
    setOpenEdit(true);
  };

  const handleCreate = () => {
    let body = { text: formText, source: formSource, type: formType };
    setOpenAdd(false);
    API_create_rag_item(body, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Create");
        fetchData();
        fetchFilterOptions();
      }
    });
  };

  const handleUpdate = () => {
    let body = { text: formText, source: formSource, type: formType };
    setOpenEdit(false);
    API_update_rag_item(selectedId, body, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Update");
        fetchData();
        fetchFilterOptions();
      }
    });
  };

  const handleDelete = () => {
    setOpenDelete(false);
    API_delete_rag_item(selectedId, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Delete");
        setSelectedId(null);
        setRowSelection([]);
        fetchData();
        fetchFilterOptions();
      }
    });
  };

  const handleSavePrompt = () => {
    API_create_system_prompt(systemPrompt, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error);
      } else {
        setApiResult(true);
        setApiMessage("Saved as version " + res.version);
        setActiveVersion(res.version);
        setIsDefault(false);
        setSelectedVersion(null);
        fetchHistory();
      }
    });
  };

  const handleActivateVersion = (version) => {
    API_activate_system_prompt(version, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error);
      } else {
        setApiResult(true);
        setApiMessage("Rolled back to version " + res.version);
        setSystemPrompt(res.prompt);
        setActiveVersion(res.version);
        setIsDefault(false);
        setSelectedVersion(null);
        fetchHistory();
      }
    });
  };

  const handleReembed = () => {
    let ids = selectedId ? [selectedId] : null;
    API_reembed_rag_items(ids, (res) => {
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Re-embed (" + (res.reembedded_count || 0) + " items)");
      }
    });
  };

  const handleChatSend = () => {
    if (!chatMessage.trim()) return;
    let msg = chatMessage.trim();
    setChatMessage("");
    setChatSending(true);
    setChatHistory((prev) => [...prev, { role: "customer", message: msg }]);

    API_chat_test_send(msg, chatSessionId, (res) => {
      setChatSending(false);
      if (res.error) {
        setApiResult(false);
        setApiMessage(res.error);
      } else {
        setChatSessionId(res.session_id);
        setChatHistory(res.history || []);
        setChatParsed({
          forward_to_sale: res.forward_to_sale,
          email: res.email,
          number_of_people: res.number_of_people,
          travel_dates: res.travel_dates,
          duration: res.duration,
        });
      }
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
  };

  const handleChatClear = () => {
    if (chatSessionId) {
      API_chat_test_clear(chatSessionId, () => {});
    }
    setChatHistory([]);
    setChatSessionId(null);
    setChatParsed({});
    setChatMessage("");
  };

  const filteredData = () => {
    if (!data) return null;
    if (!searchText) return data;
    let s = searchText.toLowerCase();
    return data.filter((item) => {
      let str = (item.text || "") + (item.source || "") + (item.type || "");
      return str.toLowerCase().includes(s);
    });
  };

  const displayData = filteredData();

  const formFields = (
    <DialogContent>
      <TextField
        autoFocus
        margin="dense"
        label="Text"
        fullWidth
        multiline
        rows={4}
        value={formText}
        onChange={(e) => setFormText(e.target.value)}
      />
      <TextField
        margin="dense"
        label="Source"
        fullWidth
        value={formSource}
        onChange={(e) => setFormSource(e.target.value)}
      />
      <TextField
        margin="dense"
        label="Type"
        fullWidth
        value={formType}
        onChange={(e) => setFormType(e.target.value)}
      />
    </DialogContent>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    let d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderSystemPrompt = () => (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 100px)",
        backgroundColor: "#F0F4F8",
      }}
    >
      {/* Left: Editor */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 50%, #1E88E5 100%)",
            px: 3.5,
            py: 2.5,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "40%",
              height: "100%",
              background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.08) 0%, transparent 70%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.5 }}>
                <TuneRoundedIcon sx={{ fontSize: 22, color: "rgba(255,255,255,0.9)" }} />
                <Typography
                  sx={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: "-0.3px" }}
                >
                  System Prompt
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12, pl: 4.2 }}>
                Configure the AI personality and behavior rules
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isDefault && activeVersion && (
                <Chip
                  label={`v${activeVersion}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    backgroundColor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    backdropFilter: "blur(4px)",
                  }}
                />
              )}
              <Chip
                label={isDefault ? "Default" : "Custom"}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  backgroundColor: isDefault
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(76,175,80,0.85)",
                  color: "#fff",
                  backdropFilter: "blur(4px)",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Editor area */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: "14px",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={20}
              maxRows={40}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Enter system prompt..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  fontSize: 13.5,
                  lineHeight: 1.9,
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  backgroundColor: "#FEFEFE",
                  padding: "20px 24px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Paper>
        </Box>

        {/* Actions bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
              {systemPrompt.length} characters
            </Typography>
            {!isDefault && activeVersion && (
              <Typography sx={{ fontSize: 12, color: "#CBD5E1" }}>
                Active: v{activeVersion}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              ...btnStyle,
              px: 3,
              py: 0.9,
              fontSize: 13,
              background: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
              boxShadow: "0 2px 8px rgba(21,101,192,0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)",
                boxShadow: "0 4px 12px rgba(21,101,192,0.35)",
              },
            }}
            onClick={handleSavePrompt}
          >
            Save as New Version
          </Button>
        </Box>
      </Box>

      {/* Right: Version History */}
      <Box
        sx={{
          width: 320,
          borderLeft: "1px solid #E2E8F0",
          backgroundColor: "#FAFBFC",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* History header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #E8ECF0",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <HistoryRoundedIcon sx={{ fontSize: 18, color: "#1565C0" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>
            Version History
          </Typography>
          <Chip
            label={promptHistory.length}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              ml: "auto",
              fontWeight: 600,
              backgroundColor: "#EBF4FF",
              color: "#1565C0",
            }}
          />
        </Box>

        {/* Version list */}
        <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
          {promptHistory.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <HistoryRoundedIcon sx={{ fontSize: 36, color: "#E2E8F0", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
                No versions yet
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#CBD5E1", mt: 0.5 }}>
                Save a prompt to create the first version
              </Typography>
            </Box>
          )}
          {promptHistory.map((item) => {
            let isActive = item.is_active;
            let isSelected = selectedVersion === item.version;
            return (
              <Box key={item.id} sx={{ px: 1.5, mb: 0.5 }}>
                <Paper
                  elevation={0}
                  onClick={() => {
                    setSelectedVersion(item.version);
                    setSystemPrompt(item.prompt);
                  }}
                  sx={{
                    p: 1.8,
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: isSelected
                      ? "1.5px solid #1565C0"
                      : isActive
                        ? "1.5px solid #4CAF50"
                        : "1.5px solid transparent",
                    backgroundColor: isSelected
                      ? "#EBF4FF"
                      : isActive
                        ? "#F0FFF4"
                        : "#fff",
                    boxShadow: isSelected
                      ? "0 2px 8px rgba(21,101,192,0.12)"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "#DBEAFE"
                        : isActive
                          ? "#E8F8ED"
                          : "#F8FAFC",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 13, color: "#1E293B" }}
                      >
                        v{item.version}
                      </Typography>
                      {isActive && (
                        <Chip
                          icon={<CheckCircleRoundedIcon sx={{ fontSize: "14px !important" }} />}
                          label="Active"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: "#E8F5E9",
                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#4CAF50" },
                          }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: "#94A3B8" }}>
                      {formatDate(item.created_at)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#64748B",
                      lineHeight: 1.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.prompt}
                  </Typography>

                  {/* Rollback button */}
                  {isSelected && !isActive && (
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      startIcon={<RestoreRoundedIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        ...btnStyle,
                        mt: 1.2,
                        fontSize: 12,
                        py: 0.5,
                        color: "#EF6C00",
                        borderColor: "#FFCC80",
                        backgroundColor: "#FFF8F0",
                        "&:hover": {
                          backgroundColor: "#FFF3E0",
                          borderColor: "#EF6C00",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActivateVersion(item.version);
                      }}
                    >
                      Activate this version
                    </Button>
                  )}
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const parsedFields = [
    { key: "forward_to_sale", label: "Forward to Sale" },
    { key: "email", label: "Email" },
    { key: "number_of_people", label: "People" },
    { key: "travel_dates", label: "Travel Dates" },
    { key: "duration", label: "Duration" },
  ];

  const renderChatPlayground = () => (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 100px)",
        backgroundColor: "#F0F4F8",
      }}
    >
      {/* Chat area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Chat header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 50%, #1E88E5 100%)",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "40%",
              height: "100%",
              background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.08) 0%, transparent 70%)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, zIndex: 1 }}>
            <ChatRoundedIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16, letterSpacing: "-0.3px" }}>
              Chat Playground
            </Typography>
            {chatSessionId && (
              <Chip
                label={`Session: ${chatSessionId.slice(0, 8)}...`}
                size="small"
                sx={{
                  fontSize: 10,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.25)",
                  height: 22,
                }}
              />
            )}
          </Box>
          <Button
            size="small"
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              ...btnStyle,
              color: "#fff",
              borderColor: "rgba(255,255,255,0.3)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              zIndex: 1,
            }}
            variant="outlined"
            onClick={handleChatClear}
          >
            New Chat
          </Button>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 3,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {chatHistory.length === 0 && (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <ChatRoundedIcon sx={{ fontSize: 48, color: "#E2E8F0", mb: 1.5 }} />
                <Typography sx={{ fontSize: 15, color: "#94A3B8", fontWeight: 500 }}>
                  Test the chatbot as a customer
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#CBD5E1", mt: 0.5 }}>
                  Type a message below to start a conversation
                </Typography>
              </Box>
            </Box>
          )}
          {chatHistory.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: msg.role === "customer" ? "flex-end" : "flex-start",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  maxWidth: "70%",
                  px: 2,
                  py: 1.2,
                  borderRadius: msg.role === "customer"
                    ? "14px 14px 4px 14px"
                    : "14px 14px 14px 4px",
                  backgroundColor: msg.role === "customer" ? "#1565C0" : "#fff",
                  color: msg.role === "customer" ? "#fff" : "#334155",
                  border: msg.role === "customer" ? "none" : "1px solid #E2E8F0",
                  boxShadow: msg.role === "customer"
                    ? "0 2px 8px rgba(21,101,192,0.2)"
                    : "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <Typography sx={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {msg.message}
                </Typography>
              </Paper>
            </Box>
          ))}
          {chatSending && (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Paper
                elevation={0}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "14px 14px 14px 4px",
                  backgroundColor: "#fff",
                  border: "1px solid #E2E8F0",
                }}
              >
                <CircularProgress size={18} sx={{ color: "#1565C0" }} />
              </Paper>
            </Box>
          )}
          <div ref={chatEndRef} />
        </Box>

        {/* Input */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#fff",
            display: "flex",
            gap: 1.2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message as a customer..."
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleChatSend();
              }
            }}
            disabled={chatSending}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontSize: 13,
                backgroundColor: "#F8FAFC",
              },
            }}
          />
          <Button
            variant="contained"
            disabled={chatSending || !chatMessage.trim()}
            onClick={handleChatSend}
            sx={{
              ...btnStyle,
              minWidth: 44,
              px: 2,
              background: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)",
              },
            }}
          >
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {/* Right: Parsed fields */}
      <Box
        sx={{
          width: 280,
          borderLeft: "1px solid #E2E8F0",
          backgroundColor: "#FAFBFC",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #E8ECF0",
            backgroundColor: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>
            Parsed Fields
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94A3B8", mt: 0.3 }}>
            Extracted from bot response
          </Typography>
        </Box>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {parsedFields.map((f) => {
            let val = chatParsed[f.key];
            let hasValue = val !== null && val !== undefined && val !== "";
            return (
              <Paper
                key={f.key}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  border: hasValue ? "1.5px solid #4CAF50" : "1.5px solid #E2E8F0",
                  backgroundColor: hasValue ? "#F0FFF4" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.3 }}>
                  {f.label}
                </Typography>
                <Typography sx={{ fontSize: 13, color: hasValue ? "#1E293B" : "#CBD5E1", fontWeight: hasValue ? 500 : 400 }}>
                  {hasValue ? String(val) : "—"}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const renderRagItems = () => (
    <Box
      sx={{
        backgroundColor: "#F0F4F8",
        height: "calc(100vh - 100px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {displayData != null ? (
        <>
          {/* Toolbar card - sticky */}
          <Box sx={{ flexShrink: 0, p: 2.5, pb: 0 }}>
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: "14px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
            >
            {/* Header bar */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 50%, #1E88E5 100%)",
                px: 3,
                py: 1.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "40%",
                  height: "100%",
                  background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.08) 0%, transparent 70%)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, zIndex: 1 }}>
                <StorageRoundedIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16, letterSpacing: "-0.3px" }}>
                  Knowledge Base
                </Typography>
                <Chip
                  label={`${total} items`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    height: 22,
                  }}
                />
              </Box>
            </Box>

            {/* Actions row */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {/* Filters */}
              <Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel sx={{ fontSize: 13 }}>Source</InputLabel>
                  <Select
                    value={filterSource}
                    label="Source"
                    onChange={(e) => setFilterSource(e.target.value)}
                    sx={{
                      borderRadius: "10px",
                      fontSize: 13,
                      backgroundColor: "#F8FAFC",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#93C5FD",
                      },
                    }}
                  >
                    <MenuItem value="">All Sources</MenuItem>
                    {sourceOptions.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel sx={{ fontSize: 13 }}>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                    sx={{
                      borderRadius: "10px",
                      fontSize: 13,
                      backgroundColor: "#F8FAFC",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#93C5FD",
                      },
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {typeOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    ...btnStyle,
                    borderColor: "#E2E8F0",
                    color: "#64748B",
                    "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
                  }}
                  onClick={() => fetchData()}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    ...btnStyle,
                    background: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
                    boxShadow: "0 2px 6px rgba(21,101,192,0.25)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)",
                    },
                  }}
                  onClick={openAddDialog}
                >
                  Add
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={selectedId == null}
                  startIcon={<EditRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    ...btnStyle,
                    borderColor: "#BBDEFB",
                    color: "#1565C0",
                    "&:hover": { borderColor: "#1565C0", backgroundColor: "#EBF4FF" },
                  }}
                  onClick={openEditDialog}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={selectedId == null}
                  startIcon={<DeleteRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    ...btnStyle,
                    borderColor: "#FFCDD2",
                    color: "#D32F2F",
                    "&:hover": { borderColor: "#D32F2F", backgroundColor: "#FFEBEE" },
                  }}
                  onClick={() => setOpenDelete(true)}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHighRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    ...btnStyle,
                    borderColor: "#CE93D8",
                    color: "#7B1FA2",
                    "&:hover": { borderColor: "#7B1FA2", backgroundColor: "#F3E5F5" },
                  }}
                  onClick={handleReembed}
                >
                  {selectedId ? "Re-embed" : "Re-embed All"}
                </Button>
              </Box>
            </Box>
          </Paper>
          </Box>

          {/* Dialogs */}
          <DialogCus
            open={openAdd}
            title="Add RAG Item"
            handleClickOpen={() => setOpenAdd(false)}
            handleClickConfirm={handleCreate}
          >
            {formFields}
          </DialogCus>
          <DialogCus
            open={openEdit}
            title="Edit RAG Item"
            handleClickOpen={() => setOpenEdit(false)}
            handleClickConfirm={handleUpdate}
          >
            {formFields}
          </DialogCus>
          <DialogCus
            open={openDelete}
            title="Confirm delete RAG item"
            handleClickOpen={() => setOpenDelete(false)}
            handleClickConfirm={handleDelete}
          />

          {/* DataGrid card */}
          <Box sx={{ flex: 1, overflow: "hidden", px: 2.5, pb: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
              "& .MuiDataGrid-root": {
                border: "none",
                fontSize: 13,
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8FAFC",
                fontSize: 13,
                fontWeight: 700,
                color: "#475569",
                borderBottom: "2px solid #E2E8F0",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.1s ease",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                },
                "&.Mui-selected": {
                  backgroundColor: "#EBF4FF",
                  borderLeft: "3px solid #1565C0",
                  "&:hover": {
                    backgroundColor: "#DBEAFE",
                  },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #F1F5F9",
                py: 1.2,
                color: "#334155",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #E2E8F0",
                backgroundColor: "#F8FAFC",
              },
              "& .MuiTablePagination-root": {
                color: "#64748B",
              },
            }}
          >
            <DataGrid
              getRowId={(item) => item.id}
              rows={displayData}
              columns={COLUMNS_RAG}
              initialState={{
                pagination: { paginationModel: { pageSize: 30 } },
              }}
              rowHeight={48}
              pageSizeOptions={[5, 10, 30, 50]}
              rowSelectionModel={rowSelection}
              onRowSelectionModelChange={(e) => {
                setRowSelection(e);
                setSelectedId(e[0] ?? null);
              }}
              density={density}
              onDensityChange={(newDensity) => setDensity(newDensity)}
            />
          </Paper>
          </Box>
        </>
      ) : (
        <Box sx={{ width: "100%", pt: 10, px: 4 }}>
          <LinearProgress
            sx={{
              borderRadius: 4,
              height: 6,
              backgroundColor: "#E2E8F0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#1565C0",
                borderRadius: 4,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <AlertCus condition={apiResult} message={apiMessage} error_text={apiMessage} />
      <AppBarSearchBox
        title="AI Chat"
        value={view === "rag" ? searchText : null}
        onChange={(e) => setSearchText(e.target.value)}
      />

      {/* View Toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #E8ECF0",
        }}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => {
            if (v) setView(v);
          }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              px: 2.5,
              py: 0.6,
              borderRadius: "20px !important",
              border: "1px solid #E0E0E0 !important",
              mx: 0.5,
              "&.Mui-selected": {
                backgroundColor: "#1565C0",
                color: "#fff",
                borderColor: "#1565C0 !important",
                "&:hover": {
                  backgroundColor: "#0D47A1",
                },
              },
            },
          }}
        >
          <ToggleButton value="rag">
            <StorageRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} />
            RAG Items
          </ToggleButton>
          <ToggleButton value="prompt">
            <TuneRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} />
            System Prompt
          </ToggleButton>
          <ToggleButton value="chat">
            <ChatRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} />
            Chat Playground
          </ToggleButton>
          <ToggleButton value="patterns">
            <QuestionAnswerRoundedIcon sx={{ fontSize: 16, mr: 0.8 }} />
            Patterns
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === "rag" && renderRagItems()}
      {view === "prompt" && renderSystemPrompt()}
      {view === "chat" && renderChatPlayground()}
      {view === "patterns" && <PatternResponse />}
    </Box>
  );
}
